import { useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Providers } from './providers'
import { Button } from '../components/Button'
import { BottomSheet } from '../components/BottomSheet'
import { Card } from '../components/Card'
import { InfoIcon, MoonIcon, RefreshIcon, SunIcon } from '../components/icons'
import { dayjs } from '../lib/time'
import { MealCards } from '../features/meals/MealCards'
import { useLunchMeals } from '../features/meals/useMeals'
import { cx } from '../lib/cx'
import { useTheme } from './theme-provider'

const SCHOOL_NAME = '한세사이버보안고등학교'

function AppInner() {
  const { theme, toggleTheme } = useTheme()
  const [baseDate, setBaseDate] = useState(() =>
    dayjs().tz('Asia/Seoul').format('YYYY-MM-DD'),
  )
  const [openAllergy, setOpenAllergy] = useState(false)
  const [showAllergies, setShowAllergies] = useState(false)

  const q = useLunchMeals(baseDate)

  const rangeLabel = useMemo(() => {
    const d0 = dayjs.tz(baseDate, 'YYYY-MM-DD', 'Asia/Seoul')
    const d3 = d0.add(3, 'day')
    return `${d0.format('MM/DD')} ~ ${d3.format('MM/DD')}`
  }, [baseDate])

  return (
    <div className="min-h-dvh">
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent" />
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="relative px-5 pt-[max(env(safe-area-inset-top),18px)] pb-4"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="text-[22px] font-black tracking-[-0.03em] leading-tight"
              >
                {SCHOOL_NAME}
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="mt-1 text-[14px] text-muted font-semibold"
              >
                중식 · {rangeLabel} (기준일 + 3일)
              </motion.div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                as={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                aria-label="테마 전환"
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={theme}
                    initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
                    animate={{ opacity: 1, rotate: 0, scale: 1 }}
                    exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
                    transition={{ duration: 0.2 }}
                  >
                    {theme === 'light' ? (
                      <MoonIcon className="h-5 w-5" />
                    ) : (
                      <SunIcon className="h-5 w-5" />
                    )}
                  </motion.div>
                </AnimatePresence>
              </Button>
              <Button
                as={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                variant="ghost"
                size="sm"
                onClick={() => setOpenAllergy(true)}
                aria-label="알레르기 안내"
              >
                <InfoIcon className="h-5 w-5" />
              </Button>
              <Button
                as={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                variant="ghost"
                size="sm"
                onClick={() => q.refetch()}
                aria-label="새로고침"
              >
                <RefreshIcon className={cx('h-5 w-5', q.isFetching ? 'animate-spin' : '')} />
              </Button>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-4 grid grid-cols-1 gap-3"
          >
            <Card className="p-4">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-[14px] font-semibold text-muted">조회일</div>
                  <input
                    type="date"
                    value={baseDate}
                    onChange={(e) => setBaseDate(e.target.value)}
                    className={cx(
                      'h-11 rounded-full border border-line bg-card px-4 text-[15px] font-semibold outline-none',
                      'focus:ring-4 focus:ring-[rgba(49,130,246,0.18)]',
                    )}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-[14px] font-semibold text-muted">알레르기 번호 표시</div>
                  <motion.button
                    type="button"
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowAllergies((v) => !v)}
                    className={cx(
                      'relative inline-flex h-8 w-14 items-center rounded-full border border-line transition-colors duration-200',
                      showAllergies ? 'bg-primary' : 'bg-muted/10',
                    )}
                    aria-pressed={showAllergies}
                    aria-label="알레르기 표시 토글"
                  >
                    <motion.span
                      layout
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      className={cx(
                        'inline-block h-6 w-6 transform rounded-full bg-white shadow-sm',
                        showAllergies ? 'ml-auto mr-1' : 'ml-1',
                      )}
                    />
                  </motion.button>
                </div>

                <div className="text-[12px] text-muted">
                  NEIS 데이터를 표시합니다. 실제 제공 여부는 학교 사정에 따라 달라질 수 있습니다.
                </div>
              </div>
            </Card>
          </motion.div>
        </motion.div>
      </header>

      <main className="px-5 pb-[max(env(safe-area-inset-bottom),20px)]">
        <MealCards
          baseDateISO={baseDate}
          data={q.data}
          isLoading={q.isLoading}
          error={q.error}
          showAllergies={showAllergies}
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-6 text-center text-[12px] text-muted"
        >
          Developed by hsnyus
        </motion.div>
      </main>

      <BottomSheet open={openAllergy} title="알레르기 번호 안내" onClose={() => setOpenAllergy(false)}>
        <div className="text-[13px] text-muted">
          급식 메뉴의 괄호 안 숫자는 알레르기 유발 식재료 번호입니다.
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            visible: { transition: { staggerChildren: 0.05 } }
          }}
          className="mt-4 grid grid-cols-2 gap-2"
        >
          {ALLERGY_LIST.map((a) => (
            <motion.div
              key={a.no}
              variants={{
                hidden: { opacity: 0, scale: 0.9 },
                visible: { opacity: 1, scale: 1 }
              }}
              className="rounded-xl border border-line bg-card px-3 py-2"
            >
              <div className="text-[13px] font-extrabold">{a.no}</div>
              <div className="text-[13px] text-muted font-semibold mt-0.5">{a.name}</div>
            </motion.div>
          ))}
        </motion.div>
        <div className="mt-4 text-[12px] text-muted">
          본 목록은 NEIS 설명에 기반합니다.
        </div>
      </BottomSheet>
    </div>
  )
}

const ALLERGY_LIST = [
  { no: 1, name: '난류(가금류)' },
  { no: 2, name: '우유' },
  { no: 3, name: '메밀' },
  { no: 4, name: '땅콩' },
  { no: 5, name: '대두' },
  { no: 6, name: '밀' },
  { no: 7, name: '고등어' },
  { no: 8, name: '게' },
  { no: 9, name: '새우' },
  { no: 10, name: '돼지고기' },
  { no: 11, name: '복숭아' },
  { no: 12, name: '토마토' },
  { no: 13, name: '아황산류' },
  { no: 14, name: '호두' },
  { no: 15, name: '닭고기' },
  { no: 16, name: '쇠고기' },
  { no: 17, name: '오징어' },
  { no: 18, name: '조개류(굴, 전복, 홍합 포함)' },
  { no: 19, name: '잣' },
]

export function App() {
  return (
    <Providers>
      <AppInner />
    </Providers>
  )
}
